import { HardHat, Building2, Waves, TrafficCone, Hammer, Layers } from 'lucide-react';
import { IDS } from '@/constants/testIds';
import { useReveal } from '@/hooks/useReveal';

const SERVICES = [
  {
    key: 'roads',
    icon: TrafficCone,
    title: 'Roads & Highways',
    description:
      'Rural roads, urban corridors, resurfacing and CC roads built to IRC & MoRTH specifications.',
    image: 'https://images.pexels.com/photos/8857393/pexels-photo-8857393.jpeg',
    span: 'md:col-span-2 md:row-span-2',
  },
  {
    key: 'buildings',
    icon: Building2,
    title: 'Public Buildings',
    description:
      'Schools, hospitals, offices and community centres for municipal & panchayati raj works.',
    image: 'https://images.unsplash.com/photo-1536895058696-a69b1c7ba34f?w=1200&q=80',
    span: 'md:col-span-2',
  },
  {
    key: 'drainage',
    icon: Waves,
    title: 'Drainage & Water',
    description:
      'Storm-water drains, sewerage networks and water supply lines executed with precision.',
    image: null,
    span: '',
  },
  {
    key: 'heavy',
    icon: Hammer,
    title: 'Heavy Civil',
    description:
      'Foundations, RCC structures, retaining walls and structural reinforcements at scale.',
    image: null,
    span: '',
  },
  {
    key: 'renovation',
    icon: Layers,
    title: 'Renovation & Upgrades',
    description:
      'Refurbishment of government buildings, heritage structures and municipal assets.',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80',
    span: 'md:col-span-2',
  },
  {
    key: 'safety',
    icon: HardHat,
    title: 'Safety-First Execution',
    description:
      'Every site follows strict PPE, quality control and environmental compliance protocols.',
    image: null,
    span: 'md:col-span-2',
  },
];

const ServiceCard = ({ service, delay }) => {
  const { ref, className } = useReveal(delay);
  const Icon = service.icon;
  return (
    <div
      ref={ref}
      data-testid={IDS.services.item(service.key)}
      className={`${className} ${service.span} group relative bg-white border border-navy-100 p-8 min-h-[220px] flex flex-col justify-between overflow-hidden hover:-translate-y-1 transition-transform duration-500`}
    >
      {service.image && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <img src={service.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy-900/75" />
        </div>
      )}

      <div className="relative z-10 flex items-start justify-between">
        <Icon className={`w-9 h-9 ${service.image ? 'text-gold-300 group-hover:text-gold-300' : 'text-gold-500'} transition-colors`} />
        <span className={`text-[10px] tracking-[0.28em] uppercase font-bold ${service.image ? 'text-white/60' : 'text-navy-400'} group-hover:text-white/70`}>
          Service
        </span>
      </div>

      <div className="relative z-10 mt-8">
        <h3 className={`font-serif text-2xl sm:text-3xl leading-tight ${service.image ? 'text-white group-hover:text-white' : 'text-navy-800'} transition-colors`}>
          {service.title}
        </h3>
        <p className={`mt-3 text-sm leading-relaxed ${service.image ? 'text-white/80 group-hover:text-white/85' : 'text-navy-600'} transition-colors max-w-md`}>
          {service.description}
        </p>
      </div>

      {/* Bottom rule */}
      <div className="relative z-10 mt-6 h-px w-12 bg-gold-400" />
    </div>
  );
};

export const Services = () => {
  const heading = useReveal(0);
  return (
    <section id="services" data-testid={IDS.services.section} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={heading.ref} className={`${heading.className} max-w-3xl mb-16`}>
          <div className="flex items-center gap-3 mb-6">
            <span className="gold-rule" />
            <span className="text-xs tracking-[0.28em] uppercase text-gold-500 font-bold">
              Capabilities
            </span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl text-navy-800 tracking-tight leading-[1.1]">
            Full-spectrum civil works for the public sector.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-navy-700/80 max-w-2xl">
            From the first survey stake to final commissioning — we handle every
            phase of a civil project with in-house teams and vetted suppliers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 auto-rows-fr">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.key} service={s} delay={i * 80} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
